import { tv } from 'tailwind-variants';

export type SemanticIntent = 'neutral' | 'neutralLight' | 'primary' | 'info' | 'warning' | 'destructive' | 'success' | 'none';

export type DisciplineIntent =
	| 'skilledNursing'
	| 'physicalTherapy'
	| 'occupationalTherapy'
	| 'speechTherapy'
	| 'homeHealthAide'
	| 'medicalSocialWork';

export type StatusIntents = SemanticIntent | DisciplineIntent;

export type StatusAppearances = 'solid' | 'solidMuted' | 'outline' | 'outlineDashed' | 'ghost' | 'subtle' | 'subtleOutline';

interface FocusSpec {
	ring: string;
	focusVisibleOutline: string;
	focusWithinRing: string;
}

interface IntentSpec {
	solid: string;
	outline: string;
	outlineDashed: string;
	ghost: string;
	subtle: string;
	subtleOutline: string;
	text: string;
}

interface InteractiveIntentSpec extends IntentSpec {
	solidInteractive: string;
	outlineInteractive: string;
	outlineDashedInteractive?: string;
	ghostInteractive: string;
	subtleInteractive?: string;
	focus: FocusSpec;
}

export const SEMANTIC_INTENTS = {
	neutral: {
		solid: 'bg-gray-700',
		outline: 'border-gray-500 bg-white text-gray-600',
		outlineDashed: 'border-gray-500 border-dashed bg-white text-gray-600',
		ghost: 'text-gray-600',
		subtle: 'bg-gray-100 text-gray-600',
		subtleOutline: 'border-gray-500 bg-gray-100 text-gray-600',
		text: 'text-gray-600',
		solidInteractive: 'active:bg-gray-900 hover:bg-gray-800',
		outlineInteractive: 'hover:bg-gray-50',
		outlineDashedInteractive: 'hover:bg-gray-50',
		ghostInteractive: 'active:bg-gray-200 hover:bg-gray-100',
		subtleInteractive: 'active:bg-gray-200 hover:bg-gray-200',
		focus: {
			ring: 'focus:border-primary-500 focus:ring-primary-500/50',
			focusVisibleOutline: 'outline-primary-500',
			focusWithinRing: 'border-primary-500 ring-primary-500/50'
		}
	},
	neutralLight: {
		solid: 'bg-gray-300',
		outline: 'border-gray-300 bg-white text-gray-500',
		outlineDashed: 'border-gray-300 border-dashed bg-white text-gray-500',
		ghost: 'text-gray-500',
		subtle: 'bg-gray-50 text-gray-500',
		subtleOutline: 'border-gray-300 bg-gray-50 text-gray-500',
		text: 'text-gray-500',
		solidInteractive: 'active:bg-gray-500 hover:bg-gray-400',
		outlineInteractive: 'hover:bg-gray-50',
		outlineDashedInteractive: 'hover:bg-gray-50',
		ghostInteractive: 'hover:bg-gray-100',
		focus: {
			ring: 'focus:border-primary-500 focus:ring-primary-500/50',
			focusVisibleOutline: 'outline-primary-500',
			focusWithinRing: 'border-primary-500 ring-primary-500/50'
		}
	},
	primary: {
		solid: 'bg-primary-500',
		outline: 'border-primary-500 text-primary-600',
		outlineDashed: 'border-primary-500 border-dashed text-primary-600',
		ghost: 'text-primary-600',
		subtle: 'bg-primary-50 text-primary-700',
		subtleOutline: 'border-primary-500 bg-primary-50 text-primary-700',
		text: 'text-primary-600',
		solidInteractive: 'active:bg-primary-700 hover:bg-primary-600',
		outlineInteractive: 'hover:bg-primary-50',
		outlineDashedInteractive: 'hover:bg-primary-50',
		ghostInteractive: 'hover:bg-primary-50',
		focus: {
			ring: 'focus:border-primary-500 focus:ring-primary-500/50',
			focusVisibleOutline: 'outline-primary-500',
			focusWithinRing: 'border-primary-500 ring-primary-500/50'
		}
	},
	info: {
		solid: 'bg-blue-600',
		outline: 'border-blue-600 text-blue-600',
		outlineDashed: 'border-blue-600 border-dashed text-blue-600',
		ghost: 'text-blue-600',
		subtle: 'bg-blue-50 text-blue-700',
		subtleOutline: 'border-blue-600 bg-blue-50 text-blue-700',
		text: 'text-blue-600',
		solidInteractive: 'active:bg-blue-800 hover:bg-blue-700',
		outlineInteractive: 'hover:bg-blue-50',
		outlineDashedInteractive: 'hover:bg-blue-50',
		ghostInteractive: 'hover:bg-blue-50',
		focus: {
			ring: 'focus:border-blue-600 focus:ring-blue-600/50',
			focusVisibleOutline: 'outline-blue-600',
			focusWithinRing: 'border-blue-600 ring-blue-600/50'
		}
	},
	warning: {
		solid: 'bg-yellow-500',
		outline: 'border-yellow-500 text-yellow-700',
		outlineDashed: 'border-yellow-500 border-dashed text-yellow-700',
		ghost: 'text-yellow-700',
		subtle: 'bg-yellow-50 text-yellow-800',
		subtleOutline: 'border-yellow-500 bg-yellow-50 text-yellow-800',
		text: 'text-yellow-700',
		solidInteractive: 'active:bg-yellow-500/80 hover:bg-yellow-500/90',
		outlineInteractive: 'hover:bg-yellow-50',
		outlineDashedInteractive: 'hover:bg-yellow-50',
		ghostInteractive: 'hover:bg-yellow-50',
		focus: {
			ring: 'focus:border-yellow-500 focus:ring-yellow-500/50',
			focusVisibleOutline: 'outline-yellow-500',
			focusWithinRing: 'border-yellow-500 ring-yellow-500/50'
		}
	},
	destructive: {
		solid: 'bg-red-600',
		outline: 'border-red-600 text-red-600',
		outlineDashed: 'border-red-600 border-dashed text-red-600',
		ghost: 'text-red-600',
		subtle: 'bg-red-50 text-red-700',
		subtleOutline: 'border-red-600 bg-red-50 text-red-700',
		text: 'text-red-600',
		solidInteractive: 'active:bg-red-800 hover:bg-red-700',
		outlineInteractive: 'hover:bg-red-50',
		ghostInteractive: 'hover:bg-red-50',
		focus: {
			ring: 'focus:border-red-600 focus:ring-red-600/50',
			focusVisibleOutline: 'outline-red-600',
			focusWithinRing: 'border-red-600 ring-red-600/50'
		}
	},
	success: {
		solid: 'bg-green-600',
		outline: 'border-green-600 text-green-700',
		outlineDashed: 'border-green-600 border-dashed text-green-700',
		ghost: 'text-green-700',
		subtle: 'bg-green-50 text-green-700',
		subtleOutline: 'border-green-600 bg-green-50 text-green-700',
		text: 'text-green-700',
		solidInteractive: 'active:bg-green-800 hover:bg-green-700',
		outlineInteractive: 'hover:bg-green-50',
		outlineDashedInteractive: 'hover:bg-green-50',
		ghostInteractive: 'hover:bg-green-50 hover:text-green-800',
		focus: {
			ring: 'focus:border-green-600 focus:ring-green-600/50',
			focusVisibleOutline: 'outline-green-600',
			focusWithinRing: 'border-green-600 ring-green-600/50'
		}
	},
	none: {
		solid: 'bg-gray-300',
		outline: 'border-gray-300 text-gray-600',
		outlineDashed: 'border-gray-300 border-dashed text-gray-600',
		ghost: 'text-gray-600',
		subtle: 'bg-gray-100 text-gray-600',
		subtleOutline: 'border-gray-300 bg-gray-100 text-gray-600',
		text: 'text-gray-600',
		solidInteractive: 'active:bg-gray-500 hover:bg-gray-400',
		outlineInteractive: 'hover:bg-gray-50',
		outlineDashedInteractive: 'hover:bg-gray-50',
		ghostInteractive: 'hover:bg-gray-100',
		focus: {
			ring: 'focus:border-primary-500 focus:ring-primary-500/50',
			focusVisibleOutline: 'outline-primary-500',
			focusWithinRing: 'border-primary-500 ring-primary-500/50'
		}
	}
} satisfies Record<SemanticIntent, InteractiveIntentSpec>;

export const DISCIPLINE_INTENTS = {
	skilledNursing: {
		solid: 'bg-primary-500',
		outline: 'border-primary-500 text-primary-500',
		outlineDashed: 'border-primary-500 border-dashed text-primary-500',
		ghost: 'text-primary-500',
		subtle: 'bg-green-50 text-primary-500',
		subtleOutline: 'border-primary-500 bg-green-50 text-primary-500',
		text: 'text-primary-500',
		solidInteractive: 'active:bg-primary-700 hover:bg-primary-600',
		outlineInteractive: 'hover:bg-primary-50',
		outlineDashedInteractive: 'hover:bg-primary-50',
		ghostInteractive: 'hover:bg-primary-50',
		focus: {
			ring: 'focus:border-primary-500 focus:ring-primary-500/50',
			focusVisibleOutline: 'outline-primary-500',
			focusWithinRing: 'border-primary-500 ring-primary-500/50'
		}
	},
	physicalTherapy: {
		solid: 'bg-blue-700',
		outline: 'border-blue-700 text-blue-700',
		outlineDashed: 'border-blue-700 border-dashed text-blue-700',
		ghost: 'text-blue-700',
		subtle: 'bg-blue-50 text-blue-700',
		subtleOutline: 'border-blue-700 bg-blue-50 text-blue-700',
		text: 'text-blue-700',
		solidInteractive: 'active:bg-blue-900 hover:bg-blue-800',
		outlineInteractive: 'hover:bg-blue-50',
		outlineDashedInteractive: 'hover:bg-blue-50',
		ghostInteractive: 'hover:bg-blue-50',
		focus: {
			ring: 'focus:border-blue-700 focus:ring-blue-700/50',
			focusVisibleOutline: 'outline-blue-700',
			focusWithinRing: 'border-blue-700 ring-blue-700/50'
		}
	},
	occupationalTherapy: {
		solid: 'bg-teal-700',
		outline: 'border-teal-700 text-teal-700',
		outlineDashed: 'border-teal-700 border-dashed text-teal-700',
		ghost: 'text-teal-700',
		subtle: 'bg-teal-50 text-teal-700',
		subtleOutline: 'border-teal-700 bg-teal-50 text-teal-700',
		text: 'text-teal-700',
		solidInteractive: 'active:bg-teal-900 hover:bg-teal-800',
		outlineInteractive: 'hover:bg-teal-50',
		outlineDashedInteractive: 'hover:bg-teal-50',
		ghostInteractive: 'hover:bg-teal-50',
		focus: {
			ring: 'focus:border-teal-700 focus:ring-teal-700/50',
			focusVisibleOutline: 'outline-teal-700',
			focusWithinRing: 'border-teal-700 ring-teal-700/50'
		}
	},
	speechTherapy: {
		solid: 'bg-amber-600',
		outline: 'border-amber-600 text-amber-600',
		outlineDashed: 'border-amber-600 border-dashed text-amber-600',
		ghost: 'text-amber-600',
		subtle: 'bg-amber-50 text-amber-700',
		subtleOutline: 'border-amber-600 bg-amber-50 text-amber-700',
		text: 'text-amber-600',
		solidInteractive: 'active:bg-amber-800 hover:bg-amber-700',
		outlineInteractive: 'hover:bg-amber-50',
		outlineDashedInteractive: 'hover:bg-amber-50',
		ghostInteractive: 'hover:bg-amber-50',
		focus: {
			ring: 'focus:border-amber-600 focus:ring-amber-600/50',
			focusVisibleOutline: 'outline-amber-600',
			focusWithinRing: 'border-amber-600 ring-amber-600/50'
		}
	},
	homeHealthAide: {
		solid: 'bg-cyan-700',
		outline: 'border-cyan-700 text-cyan-700',
		outlineDashed: 'border-cyan-700 border-dashed text-cyan-700',
		ghost: 'text-cyan-700',
		subtle: 'bg-cyan-50 text-cyan-700',
		subtleOutline: 'border-cyan-700 bg-cyan-50 text-cyan-700',
		text: 'text-cyan-700',
		solidInteractive: 'active:bg-cyan-900 hover:bg-cyan-800',
		outlineInteractive: 'hover:bg-cyan-50',
		outlineDashedInteractive: 'hover:bg-cyan-50',
		ghostInteractive: 'hover:bg-cyan-50',
		focus: {
			ring: 'focus:border-cyan-700 focus:ring-cyan-700/50',
			focusVisibleOutline: 'outline-cyan-700',
			focusWithinRing: 'border-cyan-700 ring-cyan-700/50'
		}
	},
	medicalSocialWork: {
		solid: 'bg-purple-700',
		outline: 'border-purple-700 text-purple-700',
		outlineDashed: 'border-purple-700 border-dashed text-purple-700',
		ghost: 'text-purple-700',
		subtle: 'bg-purple-50 text-purple-700',
		subtleOutline: 'border-purple-700 bg-purple-50 text-purple-700',
		text: 'text-purple-700',
		solidInteractive: 'active:bg-purple-900 hover:bg-purple-800',
		outlineInteractive: 'hover:bg-purple-50',
		outlineDashedInteractive: 'hover:bg-purple-50',
		ghostInteractive: 'hover:bg-purple-50',
		focus: {
			ring: 'focus:border-purple-700 focus:ring-purple-700/50',
			focusVisibleOutline: 'outline-purple-700',
			focusWithinRing: 'border-purple-700 ring-purple-700/50'
		}
	}
} satisfies Record<DisciplineIntent, InteractiveIntentSpec>;

export const INTENTS: Record<StatusIntents, InteractiveIntentSpec> = { ...SEMANTIC_INTENTS, ...DISCIPLINE_INTENTS };

const intentEntries = Object.entries(INTENTS) as [StatusIntents, InteractiveIntentSpec][];

export const statusVariants = tv({
	base: 'border',
	variants: {
		intent: Object.fromEntries(intentEntries.map(([intent]) => [intent, ''])) as Record<StatusIntents, string>,
		appearance: {
			solid: 'text-white',
			solidMuted: 'text-white opacity-50',
			outline: 'bg-transparent',
			outlineDashed: 'bg-transparent',
			ghost: 'border-transparent bg-transparent',
			subtle: 'border-transparent',
			subtleOutline: ''
		},
		isDisabled: {
			true: ''
		}
	},
	compoundVariants: [
		...intentEntries.flatMap(([intent, spec]) => [
			{ intent, appearance: 'solid' as const, class: spec.solid },
			{ intent, appearance: 'solidMuted' as const, class: spec.solid },
			{ intent, appearance: 'outline' as const, class: spec.outline },
			{ intent, appearance: 'outlineDashed' as const, class: spec.outlineDashed },
			{ intent, appearance: 'ghost' as const, class: spec.ghost },
			{ intent, appearance: 'subtle' as const, class: spec.subtle },
			{ intent, appearance: 'subtleOutline' as const, class: spec.subtleOutline }
		]),
		{ appearance: 'solid' as const, isDisabled: true, class: 'border-gray-200 bg-gray-100 text-gray-400' },
		{ appearance: 'solidMuted' as const, isDisabled: true, class: 'border-gray-200 bg-gray-100 text-gray-400' },
		{ appearance: 'outline' as const, isDisabled: true, class: 'border-gray-200 text-gray-400' },
		{ appearance: 'outlineDashed' as const, isDisabled: true, class: 'border-dashed border-gray-200 text-gray-400' },
		{ appearance: 'ghost' as const, isDisabled: true, class: 'text-gray-400' },
		{ appearance: 'subtle' as const, isDisabled: true, class: 'bg-gray-100 text-gray-400' },
		{ appearance: 'subtleOutline' as const, isDisabled: true, class: 'border-gray-200 bg-gray-100 text-gray-400' }
	],
	defaultVariants: {
		intent: 'neutral',
		appearance: 'subtle',
		isDisabled: false
	}
});

export const interactiveIntentCompounds = intentEntries.flatMap(([intent, spec]) => {
	const compounds: { intent: StatusIntents; appearance: StatusAppearances; isInteractive: true; class: string }[] = [
		{ intent, appearance: 'solid', isInteractive: true, class: spec.solidInteractive },
		{ intent, appearance: 'solidMuted', isInteractive: true, class: spec.solidInteractive },
		{ intent, appearance: 'outline', isInteractive: true, class: spec.outlineInteractive },
		{ intent, appearance: 'ghost', isInteractive: true, class: spec.ghostInteractive }
	];
	if (spec.subtleInteractive) {
		compounds.push({ intent, appearance: 'subtle', isInteractive: true, class: spec.subtleInteractive });
	}
	return compounds;
});

export function getStatusColor(intent: StatusIntents): string {
	return INTENTS[intent]?.text ?? 'text-gray-600';
}
